import React, { useState, useEffect } from 'react';
import {
    getTasksForProject,
    getBatchCandidates,
    generateScript,
    Task,
    BatchCandidate,
    Script,
} from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

const VIDEO_FORMATS = [
    { key: 'short_form',  label: 'Short-form', duration: '60-90 sec' },
    { key: 'explainer',   label: 'Explainer',  duration: '2-3 min'  },
    { key: 'ad_spot',     label: 'Ad Spot',    duration: '15-30 sec' },
    { key: 'long_form',   label: 'Long-form',  duration: '5-10 min'  },
];

type Step = 'setup' | 'running' | 'done';

interface Props {
    projectId: string;
    onClose: () => void;
    onComplete: (newScripts: Script[]) => void;
}

const BatchGenerateModal: React.FC<Props> = ({ projectId, onClose, onComplete }) => {
    const [step, setStep] = useState<Step>('setup');

    // Setup state
    const [calendarTasks, setCalendarTasks] = useState<Task[]>([]);
    const [calendarTaskId, setCalendarTaskId] = useState('');
    const [videoFormat, setVideoFormat] = useState('');
    const [loadingCalendars, setLoadingCalendars] = useState(true);

    // Running state
    const [candidates, setCandidates] = useState<BatchCandidate[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentTitle, setCurrentTitle] = useState('');

    // Done state
    const [generated, setGenerated] = useState<Script[]>([]);
    const [skipped, setSkipped] = useState(0);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        getTasksForProject(projectId)
            .then(tasks => {
                const calendars = tasks.filter(
                    t => t.input_data?.quick_win_type === 'content_calendar'
                        && t.output_data?.calendar?.length > 0
                );
                setCalendarTasks(calendars);
                if (calendars.length === 1) setCalendarTaskId(calendars[0].task_id);
            })
            .finally(() => setLoadingCalendars(false));
    }, [projectId]);

    const handleStart = async () => {
        if (!calendarTaskId || !videoFormat) return;
        setStep('running');

        let candidateList: BatchCandidate[] = [];
        try {
            candidateList = await getBatchCandidates(calendarTaskId, videoFormat);
        } catch {
            setErrors(['Failed to load approved articles from the calendar.']);
            setStep('done');
            return;
        }

        setCandidates(candidateList);

        const newScripts: Script[] = [];
        const newErrors: string[] = [];
        let skippedCount = 0;

        for (let i = 0; i < candidateList.length; i++) {
            const c = candidateList[i];
            setCurrentTitle(c.title);  // show which item is in progress

            if (c.has_script) {
                skippedCount++;
                setCurrentIndex(i + 1);  // skip counts as immediately done
                continue;
            }

            try {
                const script = await generateScript({
                    project_id: projectId,
                    task_id: c.task_id,
                    video_format: videoFormat,
                    num_characters: 1,
                });
                newScripts.push(script);
            } catch (err: any) {
                newErrors.push(`"${c.title}": ${getApiError(err, err.message || 'Generation failed')}`);
            }

            setCurrentIndex(i + 1);  // advance counter only after work completes
        }

        setGenerated(newScripts);
        setSkipped(skippedCount);
        setErrors(newErrors);
        setStep('done');
        if (newScripts.length > 0) onComplete(newScripts);
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (step === 'running') return; // block dismiss while running
        if (e.target === e.currentTarget) onClose();
    };

    const toGenerate = candidates.filter(c => !c.has_script).length;
    const progressPct = candidates.length > 0
        ? Math.round((currentIndex / candidates.length) * 100)
        : 0;

    return (
        <div className="script-modal-backdrop" onClick={handleBackdropClick}>
            <div className="script-modal batch-modal">

                {/* Header */}
                <div className="script-modal-header">
                    <div className="script-modal-title-row">
                        <h2 className="script-modal-title">Batch Generate Scripts</h2>
                    </div>
                    {step !== 'running' && (
                        <button className="script-modal-close" onClick={onClose} aria-label="Close">✕</button>
                    )}
                </div>

                {/* SETUP */}
                {step === 'setup' && (
                    <div className="script-modal-body view-mode batch-setup">
                        {loadingCalendars ? (
                            <p className="batch-hint">Loading content calendars…</p>
                        ) : calendarTasks.length === 0 ? (
                            <p className="batch-hint">No content calendars found for this project. Generate a content calendar first.</p>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label>Content Calendar</label>
                                    <select
                                        value={calendarTaskId}
                                        onChange={e => setCalendarTaskId(e.target.value)}
                                    >
                                        <option value="">Select a calendar…</option>
                                        {calendarTasks.map(t => (
                                            <option key={t.task_id} value={t.task_id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group" style={{ marginTop: 16 }}>
                                    <label>Video Format</label>
                                    <div className="format-options">
                                        {VIDEO_FORMATS.map(fmt => (
                                            <button
                                                key={fmt.key}
                                                type="button"
                                                className={`format-option ${videoFormat === fmt.key ? 'selected' : ''}`}
                                                onClick={() => setVideoFormat(fmt.key)}
                                            >
                                                <div className="format-option-name">{fmt.label}</div>
                                                <div className="format-option-duration">{fmt.duration}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <p className="batch-hint">
                                    Only approved articles with content will be included. Articles that already
                                    have a script in the selected format will be skipped.
                                </p>
                            </>
                        )}
                    </div>
                )}

                {/* RUNNING */}
                {step === 'running' && (
                    <div className="script-modal-body view-mode batch-running">
                        <div className="batch-progress-label">
                            {currentIndex} of {candidates.length} — <em>{currentTitle}</em>
                        </div>
                        <div className="batch-progress-track">
                            <div className="batch-progress-fill" style={{ width: `${progressPct}%` }} />
                        </div>
                        <p className="batch-hint">Please wait — generating scripts sequentially…</p>
                    </div>
                )}

                {/* DONE */}
                {step === 'done' && (
                    <div className="script-modal-body view-mode batch-done">
                        <div className="batch-summary">
                            <div className="batch-stat batch-stat-generated">
                                <span className="batch-stat-number">{generated.length}</span>
                                <span className="batch-stat-label">Generated</span>
                            </div>
                            <div className="batch-stat batch-stat-skipped">
                                <span className="batch-stat-number">{skipped}</span>
                                <span className="batch-stat-label">Skipped</span>
                            </div>
                            {errors.length > 0 && (
                                <div className="batch-stat batch-stat-errors">
                                    <span className="batch-stat-number">{errors.length}</span>
                                    <span className="batch-stat-label">Errors</span>
                                </div>
                            )}
                        </div>
                        {errors.length > 0 && (
                            <div className="batch-error-list">
                                {errors.map((e, i) => (
                                    <div key={i} className="batch-error-item">{e}</div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="script-modal-footer">
                    {step === 'setup' && (
                        <>
                            <button className="btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={handleStart}
                                disabled={!calendarTaskId || !videoFormat || loadingCalendars}
                            >
                                Start Batch
                            </button>
                        </>
                    )}
                    {step === 'running' && (
                        <span className="batch-hint" style={{ margin: 0 }}>Generation in progress…</span>
                    )}
                    {step === 'done' && (
                        <button className="btn-primary" onClick={onClose}>Done</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BatchGenerateModal;
